import { ILoggedCustomer } from "api/types";
import { ApolloClient } from "apollo-boost";
import { CustomerDataContext } from "components/Gates/CustomerDataGate";
import LockScreen from "components/Gates/LockScreen/LockScreen";
import LogoutButton from "components/Gates/LockScreen/LogoutButton";
import { History } from "history";
import Hashes from "jshashes";
import * as React from "react";
import { ApolloConsumer } from "react-apollo";
import { isBudtenderAdministeredRoute } from "router";
import strings from "static/strings.js";
import { validateTenantUserPin } from "utils/apis";

export interface ILockGateState {
  locked: boolean;
  idle: boolean;
}

export const LockGateContext = React.createContext<ILockGateState>({
  locked: false,
  idle: false
});

interface IProps {
  className?: string;

  history: History;
  children: any;
  inactivityPermittedDuration: number;
}

interface IState {
  currentControl: string;
  lastUserActivityTime?: number;
  userActivityOn?: boolean;
  userActivityTimer?: number;
  idle?: boolean;
  unlockHashedPin?: string;
  pin?: string;
  unlockPhone?: string;
  phone?: string;
  wrongInput?: boolean;
  locked?: boolean;
}

class LockGate extends React.Component<
  IProps & {
    loggedCustomer: ILoggedCustomer | undefined;
    client: ApolloClient<any>;
  },
  IState
> {
  constructor(
    props: IProps & {
      loggedCustomer: ILoggedCustomer | undefined;
      client: ApolloClient<any>;
    }
  ) {
    super(props);
    this.state = {
      locked: false,
      currentControl: "phone",
      wrongInput: false
    };
  }

  public render() {
    const { className, children } = this.props;
    const { locked, idle } = this.state;

    return (
      <div
        className={className}
        onTouchStartCapture={locked ? undefined : this.onActivityStarted}
        onTouchEndCapture={locked ? undefined : this.onActivityEnded}
      >
        <LockGateContext.Provider
          value={{
            locked: locked || false,
            idle: idle || false
          }}
        >
          {children}
        </LockGateContext.Provider>
        {locked && this.renderLockScreen()}
      </div>
    );
  }

  public async componentDidMount() {
    this.setState(state => ({
      userActivityTimer:
        state.userActivityTimer ||
        window.setInterval(this.checkUserActivity, 1000),
      lastUserActivityTime: new Date().getTime(),
      locked: false
    }));
  }

  public componentWillUnmount() {
    if (this.state.userActivityTimer) {
      window.clearInterval(this.state.userActivityTimer);
    }
  }

  public componentDidUpdate(
    prevProps: IProps & {
      loggedCustomer: ILoggedCustomer | undefined;
      client: ApolloClient<any>;
    }
  ) {
    if (prevProps.loggedCustomer !== this.props.loggedCustomer) {
      this.setState({
        lastUserActivityTime: new Date().getTime()
      });
    }
  }

  private onActivityStarted = (e: any) => {
    this.setState(state => ({
      userActivityOn: true,
      lastUserActivityTime: state.locked
        ? state.lastUserActivityTime
        : new Date().getTime()
    }));
  };

  private onActivityEnded = (e: any) => {
    this.setState(state => ({
      userActivityOn: false,
      lastUserActivityTime: state.locked
        ? state.lastUserActivityTime
        : new Date().getTime()
    }));
  };

  private checkUserActivity = () => {
    this.setState(state => {
      let customerSessionToken: string | undefined;
      let unlockHashedPin: string | undefined;
      let unlockPhone: string | undefined;

      if (this.props.loggedCustomer) {
        customerSessionToken = this.props.loggedCustomer.authToken;
        unlockHashedPin = this.props.loggedCustomer.hashedPin;
        unlockPhone = this.props.loggedCustomer.customer.phoneNumber;
      }

      const { userActivityOn, lastUserActivityTime } = state;
      const currentTime = new Date().getTime();
      const idleDuration = userActivityOn
        ? 0
        : currentTime - (lastUserActivityTime || currentTime);
      const idle = this.props.inactivityPermittedDuration - idleDuration <= 0;

      const locked = !!customerSessionToken && idle;
      const locking = locked && state.locked !== locked;
      return {
        idle,
        locked,
        unlockHashedPin,
        unlockPhone,
        pin: locking ? "" : state.pin,
        phone: locking ? "" : state.phone
      };
    });
  };

  private renderLockScreen = () => {
    const {
      currentControl,
      unlockHashedPin,
      pin,
      phone,
      wrongInput
    } = this.state;
    const pinGen = "﹡﹡﹡﹡".slice(0, (pin || "").length);
    const budtenderCanUnlock = isBudtenderAdministeredRoute(this.props.history);
    return (
      <LockScreen
        unlockHashedPin={unlockHashedPin}
        budtenderCanUnlock={budtenderCanUnlock}
        pin={pinGen}
        wrongPin={wrongInput}
        value={this.formatPhoneNumber(phone || "")}
        currentControl={currentControl}
        onClick={this.onFocus("phone")}
        onClick1={this.onFocus("pin")}
        onTap={this.onTap}
        logoutComponent={<LogoutButton callback={this.reset} />}
      />
    );
  };

  private reset = () =>
    this.setState({
      locked: false,
      pin: "",
      phone: ""
    });

  private logout = () => {
    this.reset();
  };

  private onFocus = (control: string) => () => {
    this.setState({ currentControl: control });
  };

  private onTap = (value: string) => {
    const { unlockHashedPin, currentControl } = this.state;

    if (currentControl === "pin" || unlockHashedPin) {
      this.onPinTap(value);
    } else if (currentControl === "phone") {
      this.onPhoneTap(value);
    }
  };

  private onPinTap = (value: string) => {
    this.setState(state => {
      const currentPin = state.pin || "";
      let newPin = currentPin;
      if (value === "back") {
        newPin = currentPin.slice(0, currentPin.length - 1);
      } else if (currentPin.length < 4) {
        newPin = `${currentPin}${value}`;
      }

      if (newPin !== currentPin) {
        return {
          pin: newPin,
          wrongInput: false
        };
      }
      return {};
    }, this.handlePin);
  };

  private onPhoneTap = (value: string) => {
    this.setState(state => {
      const currentPhone = state.phone || "";
      let newPhone = currentPhone;
      if (value === "back") {
        newPhone = currentPhone.slice(0, currentPhone.length - 1);
      } else if (currentPhone.length < 10) {
        newPhone = `${currentPhone}${value}`;
      }

      if (newPhone !== currentPhone) {
        return {
          phone: newPhone,
          wrongInput: false
        };
      }
      return {};
    }, this.handlePhone);
  };

  private handlePin = async () => {
    const { pin, unlockHashedPin } = this.state;
    if (!pin || pin.length < 4) {
      return;
    }

    if (new Hashes.MD5().hex(pin) === unlockHashedPin) {
      this.unlockUser();
    } else if (isBudtenderAdministeredRoute(this.props.history)) {
      try {
        const pinValidity: any = await validateTenantUserPin(
          this.props.client,
          pin
        );
        if (!pinValidity || pinValidity.invalidityCause) {
          this.setState({ wrongInput: true });
        } else {
          this.unlockUser();
        }
      } catch (err) {
        console.error(`couldn't validate ${strings.cashier} pin`, err);
      }
    } else {
      this.setState({
        wrongInput: true
      });
    }
  };

  private handlePhone = () => {
    const { phone, unlockPhone = "" } = this.state;
    if (!phone || phone.length < 10) {
      return;
    }
    if (phone === unlockPhone.substr(unlockPhone.length - 10)) {
      this.unlockUser();
    } else {
      this.setState({
        wrongInput: true
      });
    }
  };

  private formatPhoneNumber = (s: string) => {
    const s2 = (s || "").replace(/\D/g, "");
    const m1 = s2.slice(0, 3);
    const m2 = s2.slice(3, 6);
    const m3 = s2.slice(6, 10);
    return `${m1 || ""}${m2 ? `-${m2}${m3 ? `-${m3}` : ""}` : ""}`;
  };

  private unlockUser = () =>
    this.setState({
      currentControl: "phone",
      locked: false,
      pin: "",
      phone: "",
      wrongInput: false,
      lastUserActivityTime: new Date().getTime()
    });
}

export default (props: IProps) => (
  <ApolloConsumer>
    {client => (
      <CustomerDataContext.Consumer>
        {loggedCustomer => (
          <LockGate
            client={client}
            loggedCustomer={loggedCustomer}
            {...props}
          />
        )}
      </CustomerDataContext.Consumer>
    )}
  </ApolloConsumer>
);



// WEBPACK FOOTER //
// ./src/components/Gates/LockGate.tsx